import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight, Clock, Lock, Mail, CreditCard } from 'lucide-react'
import SEO from '@components/SEO'
import QuizProgress from '@components/certification/QuizProgress'
import QuestionCard from '@components/certification/QuestionCard'
import ResultsSummary from '@components/certification/ResultsSummary'
import CertificatePanel from '@components/certification/CertificatePanel'
import globalQuiz from '../data/certification/global.json'
import indiaQuiz from '../data/certification/india.json'
import { scoreCertification, questionPoints } from '@lib/certificationGrade'
import { trackEngagement } from '@lib/analytics'
import {
  getFreeAttemptUsed,
  setFreeAttemptUsed,
  clearFreeAttemptUsed,
  saveLastCertificateRecord
} from '@lib/certificationAttempts'
import { saveCertificationSession, loadCertificationSession } from '@lib/certificationSession'
import {
  fetchCertificationTrackDoc,
  fetchLatestCertificationAttemptForTrack,
  persistCertificationSubmit
} from '@lib/certificationFirestore'
import { useFirebaseAuth } from '@context/FirebaseAuthContext'
import { createCertificateId } from '@lib/generateCertificatePdf'
import {
  CERTIFICATION_EXAM_DURATION_MS,
  CERTIFICATION_MAX_POINTS,
  CERTIFICATION_PASS_POINTS
} from '@lib/certificationConfig'
import { saveExamDraft, loadExamDraft, clearExamDraft } from '@lib/certificationExamDraft'
import { saveCertificationRegistrant, loadCertificationRegistrant } from '@lib/certificationRegistrant'
import {
  CERTIFICATION_BTN_OUTLINE_DARK,
  CERTIFICATION_PAYMENT_URL,
  CERTIFICATION_PAYMENTS_LIVE,
} from '@lib/certificationUi'

const QUIZ_BY_TRACK = {
  global: globalQuiz,
  india: indiaQuiz
}

const TRACK_LABEL = {
  global: 'Global',
  india: 'India'
}

function initialValueForType(type) {
  if (type === 'ms') return []
  return null
}

function hasAnswer(question, value) {
  if (question.type === 'tf') return typeof value === 'boolean'
  if (question.type === 'mc') return typeof value === 'string' && value.length > 0
  if (question.type === 'ms') return Array.isArray(value) && value.length > 0
  return false
}

/** Countdown display for the assessment timer (H:MM:SS). */
function formatRemainingClock(totalMs) {
  const s = Math.max(0, Math.floor(totalMs / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

function certificationPrefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Scrolls a landmark into view after layout/animation settle; respects reduced motion.
 * @param {() => HTMLElement | null} getEl
 * @param {{ delayMs?: number }} [opts]
 * @returns {() => void} cancel pending scroll
 */
function scheduleCertificationScrollIntoView(getEl, opts = {}) {
  if (typeof window === 'undefined') return () => {}
  const delayMs = typeof opts.delayMs === 'number' ? opts.delayMs : 100
  const reduced = certificationPrefersReducedMotion()
  let timeoutId = 0
  let raf1 = 0
  let raf2 = 0
  timeoutId = window.setTimeout(() => {
    const el = typeof getEl === 'function' ? getEl() : null
    if (!el) return
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        el.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' })
      })
    })
  }, delayMs)
  return () => {
    window.clearTimeout(timeoutId)
    window.cancelAnimationFrame(raf1)
    window.cancelAnimationFrame(raf2)
  }
}

function syncSessionFromServer(trackId, resultPayload, certificateId, completedDateLabel, displayName) {
  saveCertificationSession(trackId, {
    result: resultPayload,
    certificateId: certificateId || '',
    completedDateLabel: completedDateLabel || '',
    displayName: displayName || ''
  })
}

export default function CertificationTrackPage() {
  const { trackId } = useParams()
  const location = useLocation()
  const { user, loading: authLoading } = useFirebaseAuth()
  const quiz = QUIZ_BY_TRACK[trackId]
  const passPoints = CERTIFICATION_PASS_POINTS
  const maxPoints = CERTIFICATION_MAX_POINTS

  const [freeUsed, setFreeUsedState] = useState(false)
  const [answers, setAnswers] = useState({})
  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState('register')
  const [result, setResult] = useState(null)
  const [displayName, setDisplayName] = useState('')
  const [completedDateLabel, setCompletedDateLabel] = useState('')
  const [certificateId, setCertificateId] = useState('')
  const [examReady, setExamReady] = useState(false)

  const [participantName, setParticipantName] = useState('')
  const [participantPhone, setParticipantPhone] = useState('')
  const [participantEmail, setParticipantEmail] = useState('')
  const [participantOrganisation, setParticipantOrganisation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [examDeadlineMs, setExamDeadlineMs] = useState(null)
  const [clockMs, setClockMs] = useState(CERTIFICATION_EXAM_DURATION_MS)

  const submitInFlightRef = useRef(false)
  const autoTimeSubmitDoneRef = useRef(false)
  const handleSubmitRef = useRef(async () => {})
  const certQuizStepRef = useRef(null)
  const certResultsAnchorRef = useRef(null)

  useEffect(() => {
    if (!trackId || !QUIZ_BY_TRACK[trackId]) {
      setExamReady(true)
      return
    }

    if (!user?.uid) {
      setExamReady(true)
      return
    }

    let cancelled = false
    setExamReady(false)

    const resetQuizUi = () => {
      setAnswers({})
      setIndex(0)
      setPhase('register')
      setResult(null)
      setDisplayName('')
      setCompletedDateLabel('')
      setCertificateId('')
      setExamDeadlineMs(null)
    }

    const hydrateResults = (resultPayload, certId, dateL, disp) => {
      clearExamDraft(trackId)
      setExamDeadlineMs(null)
      setPhase('results')
      setResult(resultPayload)
      setCertificateId(certId || '')
      setCompletedDateLabel(dateL || '')
      setDisplayName(disp || '')
      setAnswers({})
      setIndex(0)
    }

    ;(async () => {
      try {
        if (cancelled) return
        const { snap } = await fetchCertificationTrackDoc(trackId)
        if (cancelled) return

        const data = snap.exists() ? snap.data() : null
        const serverFree = data?.freeUsed === true

        if (serverFree) {
          setFreeUsedState(true)
          setFreeAttemptUsed(trackId)

          let restored = false
          if (data?.resultJson) {
            try {
              const parsed = JSON.parse(data.resultJson)
              hydrateResults(
                parsed,
                data.certificateId,
                data.completedDateLabel,
                data.displayName
              )
              syncSessionFromServer(
                trackId,
                parsed,
                data.certificateId,
                data.completedDateLabel,
                data.displayName
              )
              restored = true
            } catch (parseErr) {
              console.warn('certification: invalid resultJson', parseErr)
            }
          }

          if (!restored) {
            const sess = loadCertificationSession(trackId)
            if (sess?.result) {
              hydrateResults(sess.result, sess.certificateId, sess.completedDateLabel, sess.displayName)
              restored = true
            }
          }

          if (!restored) {
            try {
              const attempt = await fetchLatestCertificationAttemptForTrack(trackId)
              if (attempt?.resultJson) {
                const parsed = JSON.parse(attempt.resultJson)
                hydrateResults(
                  parsed,
                  attempt.certificateId,
                  attempt.completedDateLabel,
                  attempt.displayName
                )
                syncSessionFromServer(
                  trackId,
                  parsed,
                  attempt.certificateId,
                  attempt.completedDateLabel,
                  attempt.displayName
                )
                restored = true
              }
            } catch (attemptErr) {
              console.warn('certification: attempt history fallback failed', attemptErr)
            }
          }

          if (!restored) {
            resetQuizUi()
            clearExamDraft(trackId)
          }
        } else {
          setFreeUsedState(false)
          clearFreeAttemptUsed(trackId)
          const sess = loadCertificationSession(trackId)
          if (sess?.result) {
            hydrateResults(sess.result, sess.certificateId, sess.completedDateLabel, sess.displayName)
            setFreeUsedState(true)
            setFreeAttemptUsed(trackId)
          } else {
            resetQuizUi()
            const qs = QUIZ_BY_TRACK[trackId]?.questions ?? []
            const draft = loadExamDraft(trackId)
            if (draft && qs.length > 0) {
              autoTimeSubmitDoneRef.current = false
              setExamDeadlineMs(draft.deadlineMs)
              setAnswers(draft.answers || {})
              setIndex(Math.min(Math.max(0, draft.index), qs.length - 1))
              setPhase('quiz')
            }
          }
        }
      } catch (err) {
        console.warn('certification: cloud init failed, using offline fallback', err)
        const localUsed = getFreeAttemptUsed(trackId)
        setFreeUsedState(localUsed)
        const sess = loadCertificationSession(trackId)
        if (localUsed && sess?.result) {
          hydrateResults(sess.result, sess.certificateId, sess.completedDateLabel, sess.displayName)
        } else if (localUsed) {
          resetQuizUi()
          clearExamDraft(trackId)
        } else {
          resetQuizUi()
          const qs = QUIZ_BY_TRACK[trackId]?.questions ?? []
          const draft = loadExamDraft(trackId)
          if (draft && qs.length > 0) {
            autoTimeSubmitDoneRef.current = false
            setExamDeadlineMs(draft.deadlineMs)
            setAnswers(draft.answers || {})
            setIndex(Math.min(Math.max(0, draft.index), qs.length - 1))
            setPhase('quiz')
          }
        }
      } finally {
        if (!cancelled) setExamReady(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [trackId, user?.uid])

  useEffect(() => {
    if (!examReady || phase !== 'register' || !trackId) return
    const r = loadCertificationRegistrant(trackId)
    if (!r) return
    setParticipantName((p) => p || r.name)
    setParticipantPhone((p) => p || r.phone)
    setParticipantEmail((p) => p || r.email)
    setParticipantOrganisation((p) => p || r.organisation)
  }, [examReady, phase, trackId])

  useEffect(() => {
    if (phase === 'register' && user?.phoneNumber) {
      setParticipantPhone((p) => p || user.phoneNumber)
    }
  }, [phase, user?.phoneNumber])

  const questions = quiz?.questions ?? []
  const current = questions[index]

  const answeredCount = useMemo(() => {
    return questions.filter((q) => hasAnswer(q, answers[q.id])).length
  }, [questions, answers])

  const setCurrentAnswer = useCallback(
    (v) => {
      if (!current) return
      setAnswers((prev) => ({ ...prev, [current.id]: v }))
    },
    [current]
  )

  const valueForCurrent = useMemo(() => {
    if (!current) return null
    const v = answers[current.id]
    if (v !== undefined) return v
    return initialValueForType(current.type)
  }, [answers, current])

  const missingIds = useMemo(() => {
    return questions.filter((q) => !hasAnswer(q, answers[q.id])).map((q) => q.id)
  }, [questions, answers])

  const progressMeta = useMemo(() => {
    if (!current) return { pointsLabel: '', tierLabel: '' }
    const pts = questionPoints(current)
    return {
      pointsLabel: `${pts} pts if correct`,
      tierLabel: current.tier === 'core' ? 'Core tier' : 'Advanced tier'
    }
  }, [current])

  const displayNameTrimmed = useMemo(() => {
    const r = trackId ? loadCertificationRegistrant(trackId) : null
    return displayName.trim() || r?.name?.trim() || participantName.trim() || 'Participant'
  }, [displayName, participantName, trackId])

  const handleBeginAssessment = () => {
    const name = participantName.trim()
    if (!name) {
      window.alert('Please enter your full name to continue.')
      return
    }
    saveCertificationRegistrant(trackId, {
      name,
      phone: participantPhone.trim(),
      email: participantEmail.trim(),
      organisation: participantOrganisation.trim()
    })
    clearExamDraft(trackId)
    const deadline = Date.now() + CERTIFICATION_EXAM_DURATION_MS
    autoTimeSubmitDoneRef.current = false
    setExamDeadlineMs(deadline)
    saveExamDraft(trackId, { deadlineMs: deadline, answers: {}, index: 0 })
    setDisplayName(name)
    setClockMs(CERTIFICATION_EXAM_DURATION_MS)
    setPhase('quiz')
  }

  const handleSubmit = async (opts = {}) => {
    const timeExpired = opts.timeExpired === true
    if (freeUsed || submitInFlightRef.current) return
    if (!timeExpired && missingIds.length > 0) {
      window.alert(`Please answer all questions. Still missing: ${missingIds.join(', ')}.`)
      return
    }
    if (timeExpired) {
      trackEngagement('certification_timer_expire', { track_id: trackId, unanswered: missingIds.length })
    }
    submitInFlightRef.current = true
    const scored = scoreCertification(questions, answers)
    const passed = scored.rawScore >= passPoints
    let newCertId = ''
    const reg = loadCertificationRegistrant(trackId)
    const nameForCert = displayName.trim() || participantName.trim() || reg?.name?.trim() || ''
    if (passed) {
      newCertId = createCertificateId(trackId)
      setCertificateId(newCertId)
      saveLastCertificateRecord(trackId, {
        certificateId: newCertId,
        completedAt: new Date().toISOString(),
        rawScore: scored.rawScore,
        trackId
      })
    } else {
      setCertificateId('')
    }

    const resultPayload = {
      ...scored,
      passed,
      passPoints,
      maxPoints: scored.maxPoints ?? maxPoints
    }
    const completedAt = new Date()
    const dateLabel = completedAt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    const completedDateIso = completedAt.toISOString().slice(0, 10)

    const phone = participantPhone.trim()
    const email = participantEmail.trim()
    const organisation = participantOrganisation.trim()

    setSubmitting(true)
    try {
      await persistCertificationSubmit(trackId, {
        resultPayload,
        certificateId: newCertId,
        completedDateLabel: dateLabel,
        completedDateIso,
        displayName: nameForCert,
        participantPhone: phone,
        participantEmail: email,
        participantOrganisation: organisation,
        quizTitle: quiz.title,
        trackLabel: TRACK_LABEL[trackId] || trackId,
        scoreText: `${scored.rawScore} / ${scored.maxPoints ?? maxPoints}`
      })
    } catch (err) {
      console.error('certification: persist failed', err)
      window.alert(
        'Your score is shown below, but we could not save it to the server. Check your connection and try again. If the problem continues, contact support.'
      )
    } finally {
      submitInFlightRef.current = false
      setSubmitting(false)
    }

    setFreeAttemptUsed(trackId)
    setFreeUsedState(true)
    setResult(resultPayload)
    setCompletedDateLabel(dateLabel)
    setPhase('results')
    setExamDeadlineMs(null)
    clearExamDraft(trackId)
    saveCertificationSession(trackId, {
      result: resultPayload,
      certificateId: newCertId,
      completedDateLabel: dateLabel,
      displayName: nameForCert
    })

    trackEngagement('certification_submit', {
      track_id: trackId,
      correct: scored.correct,
      raw_score: scored.rawScore,
      passed,
      time_expired: timeExpired
    })
  }

  handleSubmitRef.current = handleSubmit

  useEffect(() => {
    if (phase !== 'quiz' || !examDeadlineMs || !trackId) return
    saveExamDraft(trackId, { deadlineMs: examDeadlineMs, answers, index })
  }, [phase, examDeadlineMs, answers, index, trackId])

  useEffect(() => {
    if (phase !== 'quiz' || examDeadlineMs == null) {
      setClockMs(CERTIFICATION_EXAM_DURATION_MS)
      return
    }
    const tick = () => {
      const r = examDeadlineMs - Date.now()
      setClockMs(r)
      if (r <= 0 && !autoTimeSubmitDoneRef.current) {
        autoTimeSubmitDoneRef.current = true
        void handleSubmitRef.current({ timeExpired: true })
      }
    }
    tick()
    const id = window.setInterval(tick, 250)
    return () => window.clearInterval(id)
  }, [phase, examDeadlineMs])

  useLayoutEffect(() => {
    if (phase !== 'quiz' || !current) return
    return scheduleCertificationScrollIntoView(() => certQuizStepRef.current, { delayMs: 100 })
  }, [phase, index, current?.id])

  useLayoutEffect(() => {
    if (phase !== 'results' || !result) return
    return scheduleCertificationScrollIntoView(() => certResultsAnchorRef.current, { delayMs: 100 })
  }, [phase, result])

  if (!quiz) {
    return <Navigate to="/certification" replace />
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-neutral-400 text-sm">Checking your sign-in…</div>
      </div>
    )
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname || `/certification/${trackId}`)
    return <Navigate to={`/certification/sign-in?next=${next}`} replace />
  }

  if (!examReady) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-neutral-400 text-sm">Loading assessment…</div>
      </div>
    )
  }

  const passed = result?.passed ?? false
  const scoreText = result ? `${result.rawScore} / ${result.maxPoints ?? maxPoints}` : ''

  if (freeUsed && !result) {
    return (
      <div className="bg-black min-h-screen pb-24">
        <SEO title={`Assessment access | ${quiz.title}`} description="Complimentary attempt already used for this track." />
        <section className="pt-14 lg:pt-16 pb-12 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950 to-black opacity-90" />
          <div className="w-full px-6 sm:px-8 lg:px-12 relative max-w-2xl mx-auto text-center">
            <Link
              to="/certification"
              className="inline-flex items-center text-sm text-primary-400 hover:text-primary-300 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All certification tracks
            </Link>
            <div className="glass-effect-dark rounded-2xl border border-primary-500/25 p-10">
              <Lock className="w-12 h-12 text-primary-400 mx-auto mb-6" />
              <h1 className="text-display-3 text-white mb-4">Complimentary attempt used</h1>
              <p className="text-body text-neutral-300 mb-4 leading-relaxed">
                You have already used your <strong className="text-white">complimentary</strong> attempt for the{' '}
                <strong className="text-white">{TRACK_LABEL[trackId] || trackId}</strong> track. We could not load your
                saved score on this page — open <strong className="text-white">My progress</strong> to see scores and
                download a certificate if you passed.
              </p>
              <p className="text-sm text-neutral-500 mb-8 leading-relaxed">
                To sit this assessment again, contact us below. Online purchase for extra attempts is coming soon.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
                <Link
                  to="/certification/my-results"
                  className={`${CERTIFICATION_BTN_OUTLINE_DARK} inline-flex items-center justify-center gap-2`}
                >
                  My progress
                </Link>
                <Link to="/contact#consultation" className="btn-accent inline-flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact us
                </Link>
                {CERTIFICATION_PAYMENTS_LIVE ? (
                  <a
                    href={CERTIFICATION_PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${CERTIFICATION_BTN_OUTLINE_DARK} gap-2`}
                  >
                    <CreditCard className="w-4 h-4" />
                    Purchase access
                  </a>
                ) : (
                  <span
                    className={`${CERTIFICATION_BTN_OUTLINE_DARK} gap-2 opacity-55 cursor-default select-none`}
                    role="status"
                  >
                    <CreditCard className="w-4 h-4 shrink-0" />
                    Coming soon
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  const passPct = Math.round((passPoints / maxPoints) * 100)

  const scoringBlock = (
    <div className="glass-effect-dark rounded-xl border border-primary-500/15 p-4 text-sm text-neutral-300 space-y-2">
      <p>
        <span className="text-primary-400 font-semibold">Scoring:</span> Questions 1–20 are worth{' '}
        <strong className="text-white">2 points</strong> each when correct; questions 21–40 are worth{' '}
        <strong className="text-white">3 points</strong> each when correct. Each incorrect answer deducts{' '}
        <strong className="text-white">1 point</strong>. Maximum score {maxPoints}. Pass mark:{' '}
        <strong className="text-white">
          {passPoints} / {maxPoints}
        </strong>{' '}
        ({passPct}%).
      </p>
      <p className="text-neutral-500 text-xs">
        You are signed in, so we can save your attempt securely. Once you start the questions, you have{' '}
        <strong className="text-neutral-400">60 minutes</strong> on the clock; when it reaches zero your attempt submits
        automatically (unanswered items count as incorrect). One complimentary try per track is included; for more
        attempts, reach out to Total Rewards Pro. Online paid checkout is coming soon.
      </p>
    </div>
  )

  const inputDark =
    'block w-full px-4 py-3 rounded-md border border-neutral-600 bg-navy-900/80 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500 font-open-sans text-sm'

  const showExamTimer = phase === 'quiz' && examDeadlineMs != null
  /** Portal + below-nav position: fixed inside Framer `motion` is viewport-buggy (transform ancestor). */
  const examTimerPortal =
    showExamTimer && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="timer"
            aria-live="polite"
            aria-atomic="true"
            aria-label={`Time remaining for this assessment: ${formatRemainingClock(clockMs)}`}
            className={`fixed top-12 lg:top-14 left-0 right-0 z-[60] print:hidden border-b shadow-lg backdrop-blur-md transition-colors supports-[backdrop-filter]:bg-navy-950/90 ${
              clockMs <= 0
                ? 'border-red-500/60 bg-red-950/95'
                : clockMs <= 60 * 1000
                  ? 'border-red-500/45 bg-black/95'
                  : clockMs <= 5 * 60 * 1000
                    ? 'border-amber-500/45 bg-black/95'
                    : 'border-primary-500/35 bg-navy-950/95'
            }`}
          >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2 sm:py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <div className="flex items-center gap-2 text-neutral-300 text-xs sm:text-sm min-w-0">
                <Clock
                  className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${
                    clockMs <= 60 * 1000 ? 'text-red-300' : clockMs <= 5 * 60 * 1000 ? 'text-amber-300' : 'text-primary-400'
                  }`}
                  aria-hidden
                />
                <span className="font-semibold text-white whitespace-nowrap">Time remaining</span>
                <span className="hidden md:inline text-neutral-500 font-normal">
                  · 60 min limit · auto-submit at 0:00:00
                </span>
              </div>
              <div
                className={`font-mono text-xl sm:text-2xl font-semibold tabular-nums tracking-tight shrink-0 ${
                  clockMs <= 60 * 1000 ? 'text-red-200' : clockMs <= 5 * 60 * 1000 ? 'text-amber-200' : 'text-primary-300'
                } ${clockMs <= 60 * 1000 && clockMs > 0 ? 'animate-pulse' : ''}`}
              >
                {formatRemainingClock(clockMs)}
              </div>
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <div className={`bg-black min-h-screen pb-24 ${showExamTimer ? 'pt-[6.25rem] lg:pt-[6.75rem]' : ''}`}>
      {examTimerPortal}

      <SEO
        title={`${quiz.title} | Total Rewards Pro`}
        description={`${quiz.subtitle} Weighted scoring out of ${maxPoints}; pass ${passPoints}+ (${passPct}%). Timed assessment: 60 minutes with auto-submit.`}
        keywords="total rewards certification, total rewards assessment, competency quiz"
      />

      <section className="pt-14 lg:pt-16 pb-8 relative print:hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 to-black opacity-90" />
        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/certification"
              className="inline-flex items-center text-sm text-primary-400 hover:text-primary-300 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All certification tracks
            </Link>
            <h1 className="text-display-2 text-white mb-3 text-balance">{quiz.title}</h1>
            <p className="text-body text-neutral-400 mb-4">{quiz.subtitle}</p>
            {scoringBlock}
          </div>
        </div>
      </section>

      <div className="w-full px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-8">
          {phase === 'register' && !freeUsed && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-effect-dark rounded-2xl border border-primary-500/20 p-6 sm:p-8 space-y-6"
            >
              <div>
                <h2 className="text-heading-1 text-white mb-2">Before we begin</h2>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  A few details help us personalise your certificate and reach you if needed.{' '}
                  <strong className="text-white">Your full name is required</strong>; other fields are optional but
                  appreciated. After you begin, a <strong className="text-white">60-minute timer</strong> runs for the
                  questions only — it stays visible at the top, saves if you refresh the same tab, and{' '}
                  <strong className="text-white">submits automatically</strong> when time runs out (unanswered questions
                  count as incorrect).
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-1.5" htmlFor="cert-participant-name">
                    Full name <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="cert-participant-name"
                    type="text"
                    autoComplete="name"
                    required
                    maxLength={120}
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value)}
                    className={inputDark}
                    placeholder="As it should appear on communications"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-1.5" htmlFor="cert-participant-phone">
                    Phone number
                  </label>
                  <input
                    id="cert-participant-phone"
                    type="tel"
                    autoComplete="tel"
                    maxLength={40}
                    value={participantPhone}
                    onChange={(e) => setParticipantPhone(e.target.value)}
                    className={inputDark}
                    placeholder="Include country code if applicable"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-1.5" htmlFor="cert-participant-email">
                    Email
                  </label>
                  <input
                    id="cert-participant-email"
                    type="email"
                    autoComplete="email"
                    maxLength={120}
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    className={inputDark}
                    placeholder="Work or personal email"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-neutral-300 mb-1.5"
                    htmlFor="cert-participant-organisation"
                  >
                    Organisation
                  </label>
                  <input
                    id="cert-participant-organisation"
                    type="text"
                    autoComplete="organization"
                    maxLength={160}
                    value={participantOrganisation}
                    onChange={(e) => setParticipantOrganisation(e.target.value)}
                    className={inputDark}
                    placeholder="Company or institution"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                <button type="button" className="btn-accent" onClick={handleBeginAssessment}>
                  Begin assessment
                  <ArrowRight className="w-4 h-4 ml-2 inline" />
                </button>
              </div>
            </motion.div>
          )}

          {phase === 'quiz' && current && (
            <motion.div
              ref={certQuizStepRef}
              key={current.id}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
              className="space-y-8 scroll-mt-[6.25rem] lg:scroll-mt-[6.75rem]"
            >
              <QuizProgress
                currentIndex={index}
                total={questions.length}
                answeredCount={answeredCount}
                pointsLabel={progressMeta.pointsLabel}
                tierLabel={progressMeta.tierLabel}
              />
              <QuestionCard
                question={current}
                value={valueForCurrent}
                onChange={setCurrentAnswer}
                questionNumber={index + 1}
              />
              <div
                className={`flex flex-col-reverse gap-3 sm:flex-row sm:items-center ${
                  index > 0 ? 'sm:justify-between' : 'sm:justify-end'
                }`}
              >
                {index > 0 ? (
                  <button type="button" className={CERTIFICATION_BTN_OUTLINE_DARK} onClick={() => setIndex((i) => Math.max(0, i - 1))}>
                    Previous
                  </button>
                ) : null}
                {index < questions.length - 1 ? (
                  <button type="button" className="btn-primary" onClick={() => setIndex((i) => i + 1)}>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2 inline" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-accent"
                    disabled={submitting}
                    onClick={() => void handleSubmit()}
                  >
                    {submitting ? 'Saving…' : 'Submit answers'}
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {phase === 'results' && result && (
            <motion.div
              ref={certResultsAnchorRef}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-10 cert-results-root scroll-mt-8 sm:scroll-mt-10"
            >
              <div className="cert-print-hide">
                <ResultsSummary
                  correct={result.correct}
                  total={result.total}
                  wrong={result.wrong}
                  passed={result.passed}
                  pointsEarned={result.pointsEarned}
                  wrongCount={result.wrongCount}
                  penaltyTotal={result.penaltyTotal}
                  rawScore={result.rawScore}
                  maxPoints={result.maxPoints}
                />
              </div>

              {!passed && freeUsed && (
                <div className="glass-effect-dark rounded-2xl border border-primary-500/20 p-6 text-center">
                  <p className="text-body text-neutral-300 mb-4">
                    Your complimentary attempt for this track is now used. To try again, reach out to us. Online
                    purchase for another attempt is coming soon.
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
                    <Link
                      to="/certification/my-results"
                      className={`${CERTIFICATION_BTN_OUTLINE_DARK} inline-flex justify-center items-center gap-2`}
                    >
                      My progress
                    </Link>
                    <Link to="/contact#consultation" className="btn-accent inline-flex justify-center items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Contact us
                    </Link>
                    {CERTIFICATION_PAYMENTS_LIVE ? (
                      <a
                        href={CERTIFICATION_PAYMENT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${CERTIFICATION_BTN_OUTLINE_DARK} justify-center gap-2`}
                      >
                        <CreditCard className="w-4 h-4" />
                        Purchase access
                      </a>
                    ) : (
                      <span
                        className={`${CERTIFICATION_BTN_OUTLINE_DARK} justify-center gap-2 opacity-55 cursor-default select-none`}
                        role="status"
                      >
                        <CreditCard className="w-4 h-4 shrink-0" />
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>
              )}

              {passed && certificateId && (
                <div className="space-y-4">
                  <label
                    className="block text-sm font-medium text-neutral-300 cert-print-hide"
                    htmlFor="cert-display-name"
                  >
                    Name on certificate
                  </label>
                  <input
                    id="cert-display-name"
                    type="text"
                    maxLength={120}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Adjust spelling if needed"
                    className={`${inputDark} max-w-md cert-print-hide`}
                  />
                  <p className="text-xs text-neutral-500 cert-print-hide">
                    This defaults to the name you entered before the assessment. It must not be empty for the
                    certificate.
                  </p>
                  <CertificatePanel
                    quizTitle={quiz.title}
                    displayName={displayNameTrimmed}
                    scoreText={scoreText}
                    completedDate={completedDateLabel}
                    certificateId={certificateId}
                    trackLabel={TRACK_LABEL[trackId] || trackId}
                  />
                  {freeUsed ? (
                    <div className="glass-effect-dark rounded-2xl border border-primary-500/20 p-6 cert-print-hide">
                      <p className="text-body text-neutral-300 mb-4">
                        Your score and certificate are above (download PDF or print). Your free attempt for this track
                        is already used — to <strong className="text-white">take this assessment again</strong>, reach
                        out to Total Rewards Pro. Online paid checkout is coming soon.
                      </p>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                        <Link
                          to="/certification/my-results"
                          className={`${CERTIFICATION_BTN_OUTLINE_DARK} inline-flex justify-center items-center gap-2`}
                        >
                          My progress
                        </Link>
                        <Link
                          to="/contact#consultation"
                          className="btn-accent inline-flex justify-center items-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          Request another attempt
                        </Link>
                        {CERTIFICATION_PAYMENTS_LIVE ? (
                          <a
                            href={CERTIFICATION_PAYMENT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${CERTIFICATION_BTN_OUTLINE_DARK} inline-flex justify-center items-center gap-2`}
                          >
                            <CreditCard className="w-4 h-4" />
                            Purchase access
                          </a>
                        ) : (
                          <span
                            className={`${CERTIFICATION_BTN_OUTLINE_DARK} inline-flex justify-center items-center gap-2 opacity-55 cursor-default select-none`}
                            role="status"
                          >
                            <CreditCard className="w-4 h-4 shrink-0" />
                            Coming soon
                          </span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="flex flex-col sm:flex-row flex-wrap gap-4 cert-print-hide">
                <Link to="/certification/my-results" className="btn-accent text-center sm:inline-flex justify-center">
                  My progress
                </Link>
                <Link to="/certification" className={`${CERTIFICATION_BTN_OUTLINE_DARK} text-center sm:inline-flex`}>
                  Back to tracks
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}
